import React, { useState } from 'react'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import MuiTableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import url from 'libraries/front-end-core/core/url'
import { MW_DEAL_DETAILS } from 'constants/controllers'
import { URL_DOWNLOAD_DEAL_ZIP, URL_MW_DEAL_DOCUMENT } from 'constants/urls'
import { DISPLAY_DATE_FORMAT } from 'constants/locale'
import selectAolName from 'redux/selectors/selectAolName'
import { LayoutHeader, HeaderAction } from 'components/dealwatch/Layout'
import Icon from 'components/shared/Icon'
import TealToolTip from 'components/TealToolTip'
import TableCell from 'components/dealwatch/TableCell'
import Table from './Table'
import cx from 'classnames'

const Document = ({ document, downloadUrl, dealId, onClickDownload }) => {
  const { dateReleased, filename, id: docId } = document
  const aol = useSelector(selectAolName)

  return (
    <MuiTableRow data-testid='document' className='w-100'>
      <TableCell cxNames='pv3'>
        {document.documentType.displayName}
      </TableCell>
      <TableCell cxNames='pv3'>
        {dayjs(dateReleased).format(DISPLAY_DATE_FORMAT)}
      </TableCell>
      <TableCell cxNames='pv3'>
        <a className='pointer underline' href={url(MW_DEAL_DETAILS, { dealId, aol }, { docId })}>
          {filename}
        </a>
      </TableCell>
      <TableCell cxNames='pv3'>
        <TealToolTip title='Download PDF' placement='top' leaveDelay={300}>
          <div
            className='mr2 pointer underline'
            onClick={() => {
              onClickDownload(
                downloadUrl(URL_MW_DEAL_DOCUMENT, { dealId, docId }),
                filename
              )
            }}
          >
            <IconButton size='small' classes={{ sizeSmall: 'pa3' }}>
              <Icon name='icon-download' className='w7 mt1 dark-sea-green-2' />
            </IconButton>
          </div>
        </TealToolTip>
      </TableCell>
    </MuiTableRow>
  )
}

const DealDocuments = ({ documents, downloadUrl, dealId, onClickDownload }) => {
  const [sortBy, setSortBy] = useState('date')
  const [sortAsc, setSortAsc] = useState(true)
  const [sortedDocs, setSortedDocs] = useState([])

  const sortDocs = (docs, by, asc) => {
    if (by === 'type') {
      return [...docs].sort((a, b) => {
        const typeA = a.documentType.displayName.toLowerCase()
        const typeB = b.documentType.displayName.toLowerCase()
        if (typeA < typeB) return asc ? -1 : 1
        if (typeA > typeB) return asc ? 1 : -1
        return 0
      })
    } else {
      return [...docs].sort((a, b) => {
        const dateA = new Date(a.dateReleased)
        const dateB = new Date(b.dateReleased)
        return asc ? dateA - dateB : dateB - dateA
      })
    }
  }

  React.useEffect(() => {
    setSortedDocs(sortDocs(documents, sortBy, sortAsc))
  }, [documents, sortBy, sortAsc])

  const sortByDate = () => {
    if (sortedDocs.length <= 1) return
    if (sortBy === 'date') {
      setSortAsc(!sortAsc)
    } else {
      setSortBy('date')
      setSortAsc(true)
    }
  }

  const sortByType = () => {
    if (sortedDocs.length <= 1) return
    if (sortBy === 'type') {
      setSortAsc(!sortAsc)
    } else {
      setSortBy('type')
      setSortAsc(true)
    }
  }

  return (
    <>
      <LayoutHeader title='Documents' spacing={0} cxNames='pv1 mb4'>
        <HeaderAction
          icon='icon-download'
          description='Download deal documents in .zip file'
          href={downloadUrl(URL_DOWNLOAD_DEAL_ZIP, { dealId })}
          onClick={onClickDownload}
        />
      </LayoutHeader>
      <Table
        data-testid='deal-documents-table'
        columnTitles={[
          <SortableTableHeader
            key='type-col'
            colKey='type-col'
            label='Type'
            active={sortBy === 'type'}
            asc={sortAsc}
            onClick={sortByType}
            title='Sort by type'
            disabled={sortedDocs.length <= 1}
          />,
          <SortableTableHeader
            key='date-of-release'
            colKey='date-of-release'
            label='Date of release'
            active={sortBy === 'date'}
            asc={sortAsc}
            onClick={sortByDate}
            title='Sort by date'
            disabled={sortedDocs.length <= 1}
          />,
          'File'
        ]}
      >
        {sortedDocs.map((document, key) => (
          <Document key={key} document={document} {...{ onClickDownload, downloadUrl, dealId }} />
        ))}
      </Table>
    </>
  )
}

const SortableTableHeader = ({ label, active, asc, onClick, title, colKey, disabled }) => (
  <span
    data-testid='sortable-header'
    data-sort-direction={active ? (asc ? 'asc' : 'desc') : undefined}
    key={colKey}
    className={cx(
      'inline-flex items-center whitespace-nowrap',
      disabled ? 'default-cursor' : 'pointer'
    )}
    onClick={disabled ? undefined : onClick}
    title={title}
    aria-disabled={disabled}
  >
    <span style={{ textDecoration: active && !disabled ? 'underline' : 'none' }}>{label}</span>&nbsp;
    <span style={{ fontSize: '1em', marginLeft: 2, textDecoration: 'none' }}>
      {disabled ? '' : asc && active && !disabled ? '▼' : '▲'}
    </span>
  </span>
)

export default DealDocuments
